import { AfterViewInit, Component, computed, OnInit, signal, TemplateRef, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, EMPTY, finalize, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { BreadcrumbService } from '../../../shared/components/breadcrumbs/breadcrumbs';
import { CreateSharedLinkRequestDto, SharedTestType } from '../../../shared/models/share-tests.model';
import { TestGroup } from '../../../shared/models/test-group.model';
import { TestResult } from '../../../shared/models/test-result.model';
import { Role } from '../../../shared/models/user.model';
import { BatchTasks } from '../../../shared/services/batch-tasks';
import { CustomUtils } from '../../../shared/services/custom-utils';
import { DatadogService } from '../../../shared/services/datadog.service';
import { FlashCallFormUtils } from '../../../shared/services/flash-call-form-utils';
import { LiveNumberTestingService, SearchParams } from '../../../shared/services/live-number-testing.service';
import { DialogRef, ModalService } from '../../../shared/services/modal.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { PricingService } from '../../../shared/services/pricing.service';
import { ReimburseUtils } from '../../../shared/services/reimburse-utils';
import { ShareTestResultsUtils } from '../../../shared/services/share-test-results-utils';
import { UsersService } from '../../../shared/services/users.service';
import { LntFormComponent } from '../lnt-form/lnt-form.component';
import { LntResultsTableComponent } from '../lnt-results-table/lnt-results-table.component';
import { LntSearchFormComponent } from '../lnt-search-form/lnt-search-form.component';

@Component({
  standalone: false,
  selector: 'app-lnt-index',
  templateUrl: 'lnt-index.component.html',
  styleUrls: ['./lnt-index.component.scss'],
})
export class LntIndexComponent implements OnInit, AfterViewInit {
  @ViewChild(LntResultsTableComponent, { static: false }) resultsTable: LntResultsTableComponent;
  @ViewChild(LntFormComponent, { static: false }) form: LntFormComponent;
  @ViewChild(LntSearchFormComponent, { static: false }) filter: LntSearchFormComponent;

  @ViewChild('batchRepeatModal', { read: TemplateRef, static: false }) batchRepeatModalTpl: TemplateRef<HTMLTemplateElement>;
  batchRepeatModalDialogRef: DialogRef;

  @ViewChild('shareTestsModalTpl', { read: TemplateRef, static: false }) shareTestsModalTpl: TemplateRef<HTMLTemplateElement>;

  @ViewChild('reimburseModalTpl', { read: TemplateRef, static: false }) reimburseModalTpl: TemplateRef<HTMLTemplateElement>;

  collapseSearch = false;
  pauseUpdateForm = false;
  pauseUpdateResults = false;
  isBatchRepeatLoading = false;
  batchCompleted = 0;
  testsPrice = 0;

  testingDisabled = null;

  loading = false;
  isAdmin = false;

  readonly REIMBURSE_CHUNK_SIZE = 100;

  // signals
  readonly selectedRows = signal<TestResult[]>([]);

  // computed
  readonly hasReimbursableTests = computed(() => this.selectedRows().some(row => ReimburseUtils.canReimburse(row)));

  readonly shareTestResultsInfo = computed<CreateSharedLinkRequestDto>(() =>
    ShareTestResultsUtils.prepareShareTestResultsInfo(this.selectedRows(), SharedTestType.LNT)
  );
  readonly hasForbiddenTestsToShare = computed(() => ShareTestResultsUtils.hasForbiddenTestsToShare(this.selectedRows(), SharedTestType.LNT));
  readonly shareTestTooltip = computed(() => ShareTestResultsUtils.getShareTestsTooltipLabel(this.selectedRows(), this.hasForbiddenTestsToShare()));

  constructor(
    public breadcrumbs: BreadcrumbService,
    public router: Router,
    public route: ActivatedRoute,
    public notificationService: NotificationService,
    public userService: UsersService,
    titleService: Title,
    private modal: ModalService,
    private pricingService: PricingService,
    private liveNumberService: LiveNumberTestingService,
    private dgService: DatadogService
  ) {
    titleService.setTitle('Test case create');
  }

  afterSave(res) {
    if (res.runForScheduler) {
      this.router.navigate(['/scheduler/create'], { queryParams: { test_case_id: res.id } }).then();
    } else {
      this.pauseUpdateResults = false;
      this.resultsTable.currentPageNumber = 1;
      this.resultsTable.update();
    }
  }

  onSubmitFilter(data) {
    if (this.resultsTable.loading) {
      return;
    }
    this.pauseUpdateResults = true;
    this.resultsTable.searchParams = data;
    this.resultsTable.currentPageNumber = 1;
    this.resultsTable.update();
  }

  ngOnInit() {
    this.breadcrumbs.root('Dashboard', ['dashboard']).add('TestCases', ['test/list']).add('Create', ['test/create'], true);
  }

  ngAfterViewInit() {
    this.userService.getAuthUser().then(u => {
      this.isAdmin = u.role === Role.ADMIN;
    });
    this.route.queryParams.subscribe(queryParams => {
      const idsParam = queryParams.ids as string | undefined;
      const phoneNumber = queryParams.phoneNumber as string | undefined;

      if (!idsParam && !phoneNumber) return;

      const interval = setInterval(() => {
        if (!this.resultsTable) {
          return;
        }
        clearInterval(interval);

        this.filter.filters.forEach(_ => {
          if (_.filterKey === 'searchById' && idsParam) _.show = true;
          if (_.filterKey === 'searchByPhoneNumbers' && phoneNumber) _.show = true;
        });

        if (idsParam) {
          const ids = queryParams.ids.split(',').map(_ => parseInt(_.trim()));
          this.filter.form.patchValue({ searchById: queryParams.ids });
          this.filter.model.ids = ids.join(' ');
          this.resultsTable.searchParams = { ids };
        }

        if (phoneNumber) {
          this.filter.model.phoneNumbers = phoneNumber;
          this.filter.form.patchValue({ phoneNumbers: phoneNumber }, { emitEvent: false });
          const searchParams = this.filter.getPopulatedModel();
          this.resultsTable.searchParams = searchParams;
        }

        this.resultsTable.currentPageNumber = 1;
        this.resultsTable.update();
      }, 1000);
    });
  }

  onRepeat(testCaseResult: TestResult) {
    this.form.loading = true;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.form.reset();
    this.form.loadTestCase(testCaseResult.testGroupId);
  }

  onCollapseSearch(data: SearchParams = null) {
    this.collapseSearch = !this.collapseSearch;
    this.pauseUpdateForm = this.collapseSearch;
    this.resultsTable.searchParams = data;
    this.resultsTable.currentPageNumber = 1;
    this.resultsTable.update();
  }

  startTasks(tasks: Observable<any>[]) {
    this.batchCompleted = 0;
    this.resultsTable.loading = true;
    new BatchTasks(tasks).run(4);
  }

  completeTask() {
    this.batchCompleted++;
    if (this.selectedRows().length === this.batchCompleted) {
      this.resultsTable.loading = false;
      this.selectedRows.set([]);
      this.isBatchRepeatLoading = false;
      this.batchRepeatModalDialogRef.close();
      this.resultsTable.resetBatch();
      this.resultsTable.update();
    }
  }

  batchClear(): void {
    this.selectedRows.set([]);
    this.resultsTable.resetBatch();
  }

  onBatchRepeat(): void {
    this.isBatchRepeatLoading = true;
    this.batchRepeatModalDialogRef = this.modal.alert().dialogClass('modal-dialog extra-large-modal').component(this.batchRepeatModalTpl).open();
    this.pricingService.getTestPrices(this.selectedRows().length).subscribe({
      next: (result: number) => {
        this.testsPrice = result;
        this.isBatchRepeatLoading = false;
      },
    });
  }

  onBatchReimburse(): void {
    const dialogRef = this.modal.alert().dialogClass('modal-dialog small-modal').component(this.reimburseModalTpl).open();
    dialogRef.result.then(confirmed => {
      if (!confirmed) return;

      const reimbursableTests = this.selectedRows().filter(row => ReimburseUtils.canReimburse(row));
      const chunks: number[][] = [];

      for (let i = 0; i < reimbursableTests.length; i += this.REIMBURSE_CHUNK_SIZE) {
        chunks.push(reimbursableTests.slice(i, i + this.REIMBURSE_CHUNK_SIZE).map(t => t.id!));
      }

      const totalChunks = chunks.length;
      let hasError = false;

      const chain$ = chunks.reduce<Observable<void>>(
        (acc$, chunkIds, index) =>
          acc$.pipe(
            switchMap(() => {
              const batchNumber = index + 1;

              return this.liveNumberService.reimburse(chunkIds).pipe(
                tap(() => {
                  const successMsg =
                    totalChunks === 1 ? 'Test cases reimbursed' : `Batch ${batchNumber} of ${totalChunks} test cases were reimbursed`;
                  this.notificationService.success(successMsg, 'Live Number Testing');
                }),
                catchError(err => {
                  hasError = true;
                  const errorMessage = err?.message;
                  const errorMsg =
                    totalChunks === 1
                      ? `Test cases reimbursement failed.`
                      : `Batch ${batchNumber} of ${totalChunks} test cases reimbursement failed.`;

                  const finalErrorMessage = errorMsg ? `${errorMsg} Reason: ${errorMessage}` : errorMsg;

                  this.notificationService.error(finalErrorMessage, 'Live Number Testing');
                  return of(void 0);
                })
              );
            })
          ),
        of(void 0)
      );

      chain$
        .pipe(
          finalize(() => {
            this.resultsTable.update();
            if (!hasError) {
              this.batchClear();
            }
          })
        )
        .subscribe();
    });
  }

  onShareTestsClick() {
    this.modal.alert().dialogClass('modal-dialog tablet-modal').component(this.shareTestsModalTpl).open();
  }

  onRepeatClick(): void {
    this.isBatchRepeatLoading = true;
    const testGroupIds = this.selectedRows().map(_ => _.testGroupId);
    const testGroupSettingsMap = {};
    testGroupIds.forEach(groupId => (testGroupSettingsMap[groupId] = this.liveNumberService.oneTestGroup(groupId)));
    forkJoin(testGroupSettingsMap)
      .pipe(switchMap(testGroupSettings => this.canRepeatFlashCallTests(testGroupSettings).pipe(map(allowed => ({ allowed, testGroupSettings })))))
      .subscribe(({ allowed, testGroupSettings }) => {
        if (!allowed) {
          this.isBatchRepeatLoading = false;
          this.notificationService.warning(`${FlashCallFormUtils.UNAVAILABLE_MESSAGE} Flash call tests cannot be repeated.`, 'Live Number Testing');
          return;
        }
        this.repeatSelectedRows(testGroupSettings);
      });
  }

  private canRepeatFlashCallTests(testGroupSettings: Record<string, TestGroup>): Observable<boolean> {
    const hasFlashCallTests = this.selectedRows().some(row => row.flashCallDetection || testGroupSettings[row.testGroupId]?.flashCallDetection);
    return hasFlashCallTests ? this.userService.getFlashCallAccess().pipe(map(access => access.enabled)) : of(true);
  }

  private repeatSelectedRows(testGroupSettings: Record<string, TestGroup>): void {
    const tasks = this.selectedRows().map(row => {
      const test: TestResult = row;
      const testGroup = CustomUtils.getMappedTestToTestGroupStructure(test, testGroupSettings[test.testGroupId]);
      console.debug(`Repeat Test start by ${this.userService.authUser.id}: `, testGroup);
      return this.liveNumberService.run(testGroup).pipe(
        map(() => {
          console.debug(`Repeat Test successfull  by ${this.userService.authUser.id}: `, testGroup);
          this.notificationService.success(`Test repeated for ID: ${test.id}`, 'Live Number Testing');
          this.completeTask();
        }),
        catchError(() => {
          this.notificationService.error(`Test repeat failed for ID: ${test.id}`, 'Live Number Testing');
          this.completeTask();
          return EMPTY; // Stop retrying and complete the observable
        })
      );
    });
    this.startTasks(tasks);
  }
}
